
import {
  Component,
  Emit,
  Ref,
  State,
  Vue,
  Watch
} from 'nuxt-property-decorator';
import TooltipButton from '../TooltipButton.vue';
import HeaderOffsetSpacer from '../HeaderOffsetSpacer.vue';
import { AppConstants } from '@/types/appConstants';
import LocalConfig from '@/store/localConfig.store';
import ClientSettings from '@/store/clientSettings.store';
import TocContent from '@/components/ui/toc/panel/TocContent.vue';
import TocBottom from '@/components/ui/toc/panel/TocBottom.vue';
import { SiteAppearance } from '@/types/pageContract';
import { touchSupported } from '@/util/touchSupported';
import { Site } from '@/types/initialData';

@Component({
  components: { HeaderOffsetSpacer, TooltipButton, TocBottom, TocContent }
})
export default class DesktopNav extends Vue {
  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.appearance)
  appearance: SiteAppearance;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site)
  site: Site;

  minWidth: number = 150;
  originalWidth: number = 0;
  originalMouseX: number = 0;
  maxWidth: number = 900;
  tolerance: number = 20;
  minifiedWidth: number = 56;
  minifiedHoverWidth: number = 237;
  currentWidth: number = 0;
  isMinified: boolean = false;
  isResizing: boolean = false;
  isHovering: boolean = false;
  isHoveringContent: boolean = false;
  debounceMouseMove = this.debounce((e) => {
    this.handleMouseMove(e);
  }, 5);

  get leftDrawerWidth(): number {
    return ClientSettings.leftDrawerWidth;
  }

  @Ref()
  desktopNavElm: HTMLElement;

  @Ref()
  desktopNavContentWrapper: HTMLElement;

  @Ref()
  tocContent: HTMLElement;

  @Ref()
  resizeBtn: HTMLButtonElement;

  @Watch('isMinified')
  onIsMinifiedChange(val: boolean): void {
    LocalConfig.updateIsMinifiedState(val);
  }

  @Watch('leftDrawerWidth')
  onLeftDrawerWidthChange(): void {
    if (!this.isResizing) {
      this.originalWidth = this.leftDrawerWidth;
      this.currentWidth = this.originalWidth;
      if (this.isMinified) {
        this.desktopNavContentWrapper.style.width = `${this.minifiedWidth}px`;
      } else {
        this.desktopNavContentWrapper.style.width = `${this.currentWidth}px`;
      }
    }
  }

  @Emit('is-resizing')
  mouseIsMoving(isResizing: boolean): boolean {
    return isResizing;
  }

  @Watch('$vuetify.breakpoint.mdAndDown')
  onBreakpointChange() {
    this.toggleMinifiedState();
  }

  mounted() {
    this.originalWidth = this.leftDrawerWidth;

    this.currentWidth = this.originalWidth;
    this.desktopNavContentWrapper.style.width = `${this.originalWidth}px`;

    if (!touchSupported()) {
      this.resizeBtn.addEventListener('mousedown', this.handleResize);
    }

    if (this.$vuetify.breakpoint.mdAndDown) {
      this.toggleMinifiedState();
    }
  }

  handleMouseEnter(): void {
    setTimeout(() => {
      if (this.isMinified && !this.isResizing) {
        this.desktopNavContentWrapper.style.width = `${this.minifiedHoverWidth}px`;
        this.tocContent.$el.style.width = `${this.minifiedHoverWidth}px`;
        this.isHovering = true;
        this.isHoveringContent = true;
      }
    }, 300);
  }

  handleMouseEnterContent(): void {
    this.handleMouseEnter();
    if (!this.isMinified) {
      this.isHoveringContent = true;
      this.tocContent.$el.style.width = `${this.currentWidth}px`;
    }
  }

  handleMouseLeave(): void {
    if (this.isMinified) {
      this.desktopNavContentWrapper.style.width = `${this.minifiedWidth}px`;
    }
    this.isHovering = false;
    this.isHoveringContent = false;
  }

  handleMouseUp(): void {
    // Updating client settings only once the user finished resizing
    // No reason to save to client settings if width less than minWidth
    if (this.currentWidth >= this.minWidth) {
      ClientSettings.setLeftDrawerWidth(this.currentWidth, this.site, true);
    }
  }

  toggleMinifiedState(): void {
    const minifiedState = !this.isMinified;
    this.setMinifiedState(minifiedState);
    LocalConfig.updateIsMinifiedState(minifiedState);

    const storedDrawerWidth = localStorage.getItem(
      AppConstants.LOCAL_STORAGE_LEFT_DRAWER
    );

    const localWidth = parseInt(
      storedDrawerWidth || String(this.leftDrawerWidth),
      10
    );
    const widthTooSmall = localWidth < this.minifiedHoverWidth;

    this.currentWidth = widthTooSmall ? this.minifiedHoverWidth : localWidth;
    this.desktopNavContentWrapper.style.width = this.isMinified
      ? `${this.minifiedWidth}px`
      : `${this.currentWidth}px`;
    this.tocContent.$el.style.width = `${this.currentWidth}px`;

    if (widthTooSmall) {
      ClientSettings.setLeftDrawerWidth(this.currentWidth, this.site);
    }
  }

  private handleResize(e): void {
    if (touchSupported() || this.$vuetify.breakpoint.mdAndDown) {
      return;
    }

    DesktopNav.pauseEvent(e);

    // Original width should come from the store value and set on mounted life cycle
    this.originalWidth = parseFloat(
      getComputedStyle(this.desktopNavElm, null)
        .getPropertyValue('width')
        .replace('px', '')
    );
    this.originalMouseX = e.pageX;
    // set mouse move, touch events and mouse up events based on click
    window.addEventListener('mousemove', this.debounceMouseMove, false);
    window.addEventListener('touchmove', this.debounceMouseMove, false);

    // Stop resizing when mouse up
    window.addEventListener('mouseup', this.stopResize, false);
    window.addEventListener('touchend', this.stopResize, false);
  }

  private handleMouseMove(e): void {
    const width = this.originalWidth + (e.pageX - this.originalMouseX);
    this.currentWidth = width;
    this.isResizing = true;
    this.mouseIsMoving(this.isResizing);

    if (width > this.maxWidth) {
      return;
    }

    // Remember to set the new value to the store
    this.desktopNavContentWrapper.style.width = `${width}px`;
    this.tocContent.$el.style.width = `${width}px`;
    if (width >= this.minWidth) {
      ClientSettings.setLeftDrawerWidth(width, this.site, false);
    }
  }

  private handleMinifiedWidth(): void {
    if (this.currentWidth > this.minWidth + this.tolerance) {
      this.setMinifiedState(false);
    } else {
      // If width is less than min width, we set the minified state to true
      this.setMinifiedState(true);
      this.desktopNavContentWrapper.style.width = `${this.minifiedWidth}px`;
      LocalConfig.updateIsMinifiedState(this.isMinified);
    }
    setTimeout(() => {
      this.isResizing = false;
      this.mouseIsMoving(this.isResizing);
    });
  }

  private setMinifiedState(isMinified: boolean): void {
    this.isMinified = isMinified;
  }

  private stopResize(e): void {
    DesktopNav.pauseEvent(e);
    // When mouse up, we want to handle the width of the wrapper width
    // and determine if should be minified
    this.handleMinifiedWidth();

    window.removeEventListener('mousemove', this.debounceMouseMove);
    window.removeEventListener('touchmove', this.debounceMouseMove);
    window.removeEventListener('mouseup', this.stopResize, false);
    window.removeEventListener('touchend', this.stopResize, false);
  }

  private debounce(callback, wait) {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(context, args), wait);
    };
  }

  private static pauseEvent(e) {
    if (e.stopPropagation) e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    e.cancelBubble = true;
    e.returnValue = false;
    return false;
  }
}
